import React, { useState, useEffect, useCallback } from "react";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import {
    Plus,
    Search,
    Trash2,
    CreditCard,
    Download,
} from "lucide-react";
import { getStatusColor, statusLabels, formatDate, formatCurrency } from "../../lib/utils";
import { InvoicePDFTemplate } from "../../components/pdf/PDFTemplates";
import { Pagination } from "../../components/ui/pagination-control";

export const InvoicesPage = () => {
    const { get, post } = useApi();
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [tenant, setTenant] = useState(null);
    const [billingSettings, setBillingSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 15;
    const [formData, setFormData] = useState({
        client_id: "",
        project_id: "",
        items: [{ description: "", quantity: 1, unit_price: 0 }],
        discount_percent: 0,
        notes: "",
    });
    const [paymentData, setPaymentData] = useState({
        amount: 0,
        method: "bank_transfer",
        reference: "",
        notes: "",
    });

    const fetchData = useCallback(async () => {
        const results = await Promise.allSettled([
            get("/invoices"),
            get("/clients"),
            get("/projects"),
            get("/settings/tenant"),
            get("/settings/billing"),
        ]);
        if (results[0].status === "fulfilled") setInvoices(results[0].value);
        else toast.error("Erreur lors du chargement des factures");
        if (results[1].status === "fulfilled") setClients(results[1].value);
        if (results[2].status === "fulfilled") setProjects(results[2].value);
        if (results[3].status === "fulfilled") setTenant(results[3].value);
        if (results[4].status === "fulfilled") setBillingSettings(results[4].value);
        setLoading(false);
    }, [get]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);    const addItem = () => {
        setFormData({
            ...formData,
            items: [...formData.items, { description: "", quantity: 1, unit_price: 0 }],
        });
    };

    const removeItem = (index) => {
        setFormData({
            ...formData,
            items: formData.items.filter((_, i) => i !== index),
        });
    };

    const updateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData({ ...formData, items: newItems });
    };

    const calculateTotal = () => {
        const subtotal = formData.items.reduce(
            (sum, item) => sum + item.quantity * item.unit_price,
            0
        );
        const discount = subtotal * (formData.discount_percent / 100);
        const tax = (subtotal - discount) * 0.2;
        return {
            subtotal,
            discount,
            tax,
            total: subtotal - discount + tax,
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.client_id) {
            toast.error("Veuillez sélectionner un client");
            return;
        }

        setSubmitting(true);
        try {
            await post("/invoices", {
                ...formData,
                project_id: formData.project_id || null,
            });
            toast.success("Facture créée avec succès");
            setDialogOpen(false);
            setFormData({
                client_id: "",
                project_id: "",
                items: [{ description: "", quantity: 1, unit_price: 0 }],
                discount_percent: 0,
                notes: "",
            });
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la création de la facture");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePayment = async (e) => {
        e.preventDefault();
        if (!selectedInvoice) return;

        setSubmitting(true);
        try {
            await post(`/invoices/${selectedInvoice.invoice_id}/payments`, paymentData);
            toast.success("Paiement enregistré");
            setPaymentDialogOpen(false);
            setSelectedInvoice(null);
            setPaymentData({ amount: 0, method: "bank_transfer", reference: "", notes: "" });
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de l'enregistrement du paiement");
        } finally {
            setSubmitting(false);
        }
    };

    const openPaymentDialog = (invoice) => {
        setSelectedInvoice(invoice);
        setPaymentData({
            amount: invoice.total - invoice.amount_paid,
            method: "bank_transfer",
            reference: "",
            notes: "",
        });
        setPaymentDialogOpen(true);
    };

    const filteredInvoices = invoices.filter(
        (invoice) =>
            invoice.invoice_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
            invoice.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalPages = Math.ceil(filteredInvoices.length / PAGE_SIZE);
    const paginatedInvoices = filteredInvoices.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const totals = calculateTotal();

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Factures</h1>
                    <p className="text-muted-foreground mt-1">{invoices.length} factures au total</p>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-invoice-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvelle facture
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Nouvelle facture</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Client *</Label>
                                    <Select
                                        value={formData.client_id}
                                        onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                                    >
                                        <SelectTrigger data-testid="invoice-client-select">
                                            <SelectValue placeholder="Sélectionner un client" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {clients.map((client) => (
                                                <SelectItem key={client.client_id} value={client.client_id}>
                                                    {client.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Projet (optionnel)</Label>
                                    <Select
                                        value={formData.project_id}
                                        onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Sélectionner un projet" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {projects.map((project) => (
                                                <SelectItem key={project.project_id} value={project.project_id}>
                                                    {project.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Items */}
                            <div>
                                <Label className="mb-2 block">Articles</Label>
                                <div className="space-y-2">
                                    {formData.items.map((item, index) => (
                                        <div key={index} className="flex gap-2 items-end">
                                            <div className="flex-1">
                                                <Input
                                                    placeholder="Description"
                                                    value={item.description}
                                                    onChange={(e) => updateItem(index, "description", e.target.value)}
                                                    data-testid={`invoice-item-description-${index}`}
                                                />
                                            </div>
                                            <div className="w-24">
                                                <Input
                                                    type="number"
                                                    placeholder="Qté"
                                                    value={item.quantity}
                                                    onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)}
                                                />
                                            </div>
                                            <div className="w-32">
                                                <Input
                                                    type="number"
                                                    placeholder="Prix unitaire"
                                                    value={item.unit_price}
                                                    onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                                                />
                                            </div>
                                            <div className="w-28 text-right font-mono text-sm">
                                                {formatCurrency(item.quantity * item.unit_price)}
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeItem(index)}
                                                disabled={formData.items.length === 1}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                                <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2">
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter un article
                                </Button>
                            </div>

                            {/* Totals */}
                            <div className="border-t pt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span>Sous-total</span>
                                    <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <span>Remise</span>
                                        <Input
                                            type="number"
                                            className="w-20 h-8"
                                            value={formData.discount_percent}
                                            onChange={(e) =>
                                                setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })
                                            }
                                        />
                                        <span>%</span>
                                    </div>
                                    <span className="font-mono">-{formatCurrency(totals.discount)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span>TVA (20%)</span>
                                    <span className="font-mono">{formatCurrency(totals.tax)}</span>
                                </div>
                                <div className="flex justify-between text-lg font-bold border-t pt-2">
                                    <span>Total TTC</span>
                                    <span className="font-mono">{formatCurrency(totals.total)}</span>
                                </div>
                            </div>

                            <div>
                                <Label>Notes</Label>
                                <Textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows={2}
                                />
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit" disabled={submitting} data-testid="submit-invoice-btn">
                                    {submitting ? "Création..." : "Créer la facture"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Payment Dialog */}
            <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Enregistrer un paiement</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handlePayment} className="space-y-4">
                        <div>
                            <Label>Montant restant dû</Label>
                            <p className="text-lg font-mono font-bold">
                                {selectedInvoice && formatCurrency(selectedInvoice.total - selectedInvoice.amount_paid)}
                            </p>
                        </div>
                        <div>
                            <Label>Montant du paiement</Label>
                            <Input
                                type="number"
                                value={paymentData.amount}
                                onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
                                data-testid="payment-amount-input"
                            />
                        </div>
                        <div>
                            <Label>Méthode de paiement</Label>
                            <Select
                                value={paymentData.method}
                                onValueChange={(value) => setPaymentData({ ...paymentData, method: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="bank_transfer">Virement bancaire</SelectItem>
                                    <SelectItem value="card">Carte bancaire</SelectItem>
                                    <SelectItem value="cash">Espèces</SelectItem>
                                    <SelectItem value="check">Chèque</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Référence</Label>
                            <Input
                                value={paymentData.reference}
                                onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} data-testid="submit-payment-btn">
                                {submitting ? "Enregistrement..." : "Enregistrer"}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Rechercher une facture..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-invoices-input"
                />
            </div>

            {/* Invoices Table */}
            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>N° Facture</TableHead>
                                <TableHead>Client</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Montant</TableHead>
                                <TableHead className="text-right">Payé</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead>PDF</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedInvoices.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                        Aucune facture trouvée
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedInvoices.map((invoice) => (
                                    <TableRow key={invoice.invoice_id} data-testid={`invoice-row-${invoice.invoice_id}`}>
                                        <TableCell className="font-mono font-medium">{invoice.invoice_number}</TableCell>
                                        <TableCell>{invoice.client_name || "-"}</TableCell>
                                        <TableCell>{formatDate(invoice.created_at)}</TableCell>
                                        <TableCell className="text-right font-mono">{formatCurrency(invoice.total)}</TableCell>
                                        <TableCell className="text-right font-mono">{formatCurrency(invoice.amount_paid)}</TableCell>
                                        <TableCell>
                                            <Badge className={getStatusColor(invoice.status)}>
                                                {statusLabels[invoice.status] || invoice.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedInvoice(invoice);
                                                    setPdfDialogOpen(true);
                                                }}
                                                data-testid={`pdf-btn-${invoice.invoice_id}`}
                                            >
                                                <Download className="w-4 h-4 mr-1" />
                                                PDF
                                            </Button>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {invoice.status !== "paid" && invoice.status !== "cancelled" && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => openPaymentDialog(invoice)}
                                                    data-testid={`payment-btn-${invoice.invoice_id}`}
                                                >
                                                    <CreditCard className="w-4 h-4 mr-1" />
                                                    Payer
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* PDF Export Dialog */}
            <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Export PDF - {selectedInvoice?.invoice_number}</DialogTitle>
                    </DialogHeader>
                    {selectedInvoice && (
                        <InvoicePDFTemplate
                            invoice={selectedInvoice}
                            tenant={tenant}
                            billingSettings={billingSettings}
                            onExport={() => {
                                toast.success("PDF téléchargé avec succès");
                                setPdfDialogOpen(false);
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Pagination
                page={currentPage}
                pages={totalPages}
                total={filteredInvoices.length}
                pageSize={PAGE_SIZE}
                onPageChange={(p) => setCurrentPage(p)}
            />
        </div>
    );
};
